import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  Download,
  FileText,
  Gift,
  Loader2,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Search,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import AuthModal from "@/components/auth/AuthModal";
import { useAuth } from "@/context/AuthContext";

const TINTS = {
  "2D Floor Plan": "bg-teal-100 text-teal-800",
  "3D Floor Plan": "bg-amber-100 text-amber-800",
  "Renew Existing Floor Plan": "bg-sky-100 text-sky-800",
  Other: "bg-stone-100 text-stone-800",
};

const STATUS_OPTIONS = ["new", "in_progress", "paid", "delivered", "cancelled"];
const STATUS_LABEL = {
  new: "New",
  in_progress: "In progress",
  paid: "Paid",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STATUS_TINT = {
  new: "bg-stone-100 text-stone-700",
  in_progress: "bg-amber-100 text-amber-800",
  paid: "bg-teal-100 text-teal-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-700",
};

const URGENCY_LABEL = {
  within_48h: "Within 48h",
  within_24h: "Within 24h",
  within_week: "Within 1 week",
};

export default function Admin() {
  const { user, loading, logout, authHeader, API } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("All");
  const [activeLead, setActiveLead] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, {
          headers: authHeader(),
          withCredentials: true,
        }),
        axios.get(`${API}/admin/stats`, {
          headers: authHeader(),
          withCredentials: true,
        }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 403) {
        setError("This account does not have admin access.");
      } else if (e?.response?.status === 401) {
        setError("Please sign in with the admin account.");
      } else {
        setError("Could not load admin data.");
      }
    }
  }, [API, authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Don't redirect — show sign-in card so admin can authenticate inline.
      setError("signed_out");
      return;
    }
    if (!user.is_admin) {
      setError("This account does not have admin access.");
      return;
    }
    setError(null);
    fetchAll();
  }, [user, loading, navigate, fetchAll]);

  const services = useMemo(() => {
    if (!leads) return ["All"];
    const set = new Set(leads.map((l) => l.service_type).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [leads]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (serviceFilter !== "All" && l.service_type !== serviceFilter) return false;
      if (!q) return true;
      const hay = [
        l.name,
        l.email,
        l.phone,
        l.property_address,
        l.message,
        l.service_type,
        l.style,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search, serviceFilter]);

  const openLead = async (lead) => {
    setDetailLoading(true);
    setActiveLead({ ...lead });
    try {
      const { data } = await axios.get(`${API}/admin/leads/${lead.id}`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setActiveLead(data);
    } catch (e) {
      console.error(e);
      toast.error("Could not load lead details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { status: newStatus },
        { headers: authHeader(), withCredentials: true }
      );
      // Update list + active drawer locally
      setLeads((prev) =>
        prev ? prev.map((l) => (l.id === leadId ? { ...l, ...data.lead } : l)) : prev
      );
      if (activeLead?.id === leadId) {
        setActiveLead((prev) => (prev ? { ...prev, ...data.lead } : prev));
      }
      if (data.reward?.awarded) {
        if (data.reward.credits_created > 0) {
          toast.success(
            `+${data.reward.awarded} pts awarded — 🎁 ${data.reward.credits_created} free credit unlocked!`
          );
        } else {
          toast.success(`+${data.reward.awarded} pts awarded to the client`);
        }
      } else {
        toast.message(`Status set to ${STATUS_LABEL[newStatus] || newStatus}`);
      }
      // Refresh stats so banner updates
      try {
        const { data: s } = await axios.get(`${API}/admin/stats`, {
          headers: authHeader(),
          withCredentials: true,
        });
        setStats(s);
      } catch (err) { console.error("admin stats refresh failed:", err); }
    } catch (e) {
      console.error(e);
      toast.error("Could not update status");
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="admin-loading">
        <Nav />
        <div className="pt-40 flex items-center justify-center text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      </main>
    );
  }

  if (error) {
    const isSignedOut = error === "signed_out";
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="admin-no-access">
        <Nav />
        <div className="pt-40 px-6 lg:px-12 max-w-2xl mx-auto">
          <div className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center">
            <div className="inline-flex h-12 w-12 rounded-full bg-rose-100 items-center justify-center mb-4">
              <ShieldCheck className="h-5 w-5 text-rose-700" />
            </div>
            <h1 className="font-display text-3xl font-semibold">
              {isSignedOut ? "Master admin sign in" : "Access restricted"}
            </h1>
            <p className="text-stone-600 mt-3">
              {isSignedOut
                ? "Sign in with the master admin account to see every customer request and download their files."
                : error}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-800 px-5 py-2.5 rounded-full text-sm font-semibold hover:border-teal-700 hover:text-teal-700 transition-colors"
                data-testid="admin-back-home-btn"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Home
              </Link>
              {isSignedOut ? (
                <button
                  type="button"
                  onClick={() => setAuthOpen(true)}
                  data-testid="admin-signin-btn"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
                >
                  <LogIn className="h-3.5 w-3.5" /> Sign in
                </button>
              ) : (
                <button
                  onClick={async () => {
                    await logout();
                    toast.message("Signed out");
                    navigate("/");
                  }}
                  data-testid="admin-noaccess-signout-btn"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              )}
            </div>
          </div>
        </div>
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode="login" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAF7F2] text-stone-900" data-testid="admin-root">
      <Nav />

      <section className="pt-32 pb-10 lg:pt-40 lg:pb-12 px-6 lg:px-12 bg-bubbly relative overflow-hidden">
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[320px] h-[320px] top-1/3 -right-24" />

        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
                <ShieldCheck className="h-3.5 w-3.5 text-teal-700" />
                Admin console
              </span>
              <h1 className="font-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.02]">
                All customer requests
              </h1>
              <p className="mt-4 text-stone-600 text-lg leading-relaxed max-w-xl">
                Every Lead, Quick Request and Portfolio Request — with uploaded
                files ready to download.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="text-xs text-stone-500 mb-1">Signed in as</div>
                <div className="flex items-center gap-2 text-stone-900 font-semibold mb-3 break-all">
                  <Mail className="h-4 w-4 text-teal-700 flex-shrink-0" /> {user?.email}
                </div>
                <button
                  onClick={async () => {
                    await logout();
                    toast.message("Signed out");
                    navigate("/");
                  }}
                  data-testid="admin-signout-btn"
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          </div>

          {stats && (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
              <StatCard label="Total leads" value={stats.total_leads} testId="stat-total" />
              <StatCard label="Today" value={stats.leads_today} testId="stat-today" tint="amber" />
              <StatCard label="Last 7 days" value={stats.leads_last_7_days} testId="stat-week" tint="teal" />
              <StatCard label="Free credits pending" value={stats.freebies_pending || 0} testId="stat-freebies" tint={stats?.freebies_pending ? "rose" : "sky"} />
            </div>
          )}

          {stats?.freebies_pending > 0 && (
            <Link
              to="/admin/clients"
              data-testid="freebie-banner"
              className="mt-6 flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-2xl p-4 hover:bg-amber-100 transition-colors"
            >
              <div className="inline-flex h-10 w-10 rounded-full bg-amber-200 items-center justify-center flex-shrink-0">
                <Gift className="h-5 w-5 text-amber-800" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  {stats.freebies_pending}{" "}
                  {stats.freebies_pending === 1 ? "client has" : "clients have"} a free
                  B&amp;W floor plan to redeem
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Open the Clients tab to redeem and deliver.
                </div>
              </div>
              <span className="text-amber-900 font-bold">→</span>
            </Link>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/admin/clients"
              data-testid="admin-clients-link"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              <Users className="h-4 w-4" /> Manage clients
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 px-6 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, address, message…"
                data-testid="admin-search-input"
                className="w-full bg-white border border-stone-200 rounded-full pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-teal-700"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="admin-service-filter">
              {services.map((s) => (
                <button
                  key={s}
                  onClick={() => setServiceFilter(s)}
                  data-testid={`filter-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${
                    serviceFilter === s
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {!leads && (
            <div className="text-center py-20 text-stone-500" data-testid="admin-table-loading">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading requests…
            </div>
          )}

          {leads && filtered.length === 0 && (
            <div
              className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center"
              data-testid="admin-empty"
            >
              <div className="inline-flex h-12 w-12 rounded-full bg-teal-100 items-center justify-center mb-4">
                <FileText className="h-5 w-5 text-teal-700" />
              </div>
              <h3 className="font-display text-xl font-semibold">No matching requests</h3>
              <p className="text-stone-600 mt-2">
                Try adjusting your search or filters.
              </p>
            </div>
          )}

          {leads && filtered.length > 0 && (
            <div
              className="bg-white border border-stone-200 rounded-[2rem] overflow-hidden"
              data-testid="admin-leads-table"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-4 font-semibold">When</th>
                      <th className="text-left px-5 py-4 font-semibold">Name</th>
                      <th className="text-left px-5 py-4 font-semibold">Contact</th>
                      <th className="text-left px-5 py-4 font-semibold">Service</th>
                      <th className="text-left px-5 py-4 font-semibold">Status</th>
                      <th className="text-left px-5 py-4 font-semibold">Address</th>
                      <th className="text-left px-5 py-4 font-semibold">Files</th>
                      <th className="text-right px-5 py-4 font-semibold">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((lead) => {
                      const tint = TINTS[lead.service_type] || TINTS.Other;
                      const created = new Date(lead.created_at);
                      return (
                        <tr
                          key={lead.id}
                          className="border-t border-stone-100 hover:bg-stone-50/60 transition-colors"
                          data-testid={`admin-lead-row-${lead.id}`}
                        >
                          <td className="px-5 py-4 align-top whitespace-nowrap text-stone-600">
                            <div className="font-medium text-stone-900">
                              {created.toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                            <div className="text-xs text-stone-500">
                              {created.toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </td>
                          <td className="px-5 py-4 align-top font-semibold">{lead.name}</td>
                          <td className="px-5 py-4 align-top text-stone-600">
                            <div className="break-all">{lead.email}</div>
                            {lead.phone && (
                              <div className="text-xs text-stone-500 mt-0.5">{lead.phone}</div>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top">
                            <span
                              className={`inline-block ${tint} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider`}
                            >
                              {lead.service_type}
                            </span>
                            {lead.style && (
                              <div className="text-xs text-stone-500 mt-1.5">
                                Style: <span className="font-semibold text-stone-700">{lead.style}</span>
                              </div>
                            )}
                            {lead.urgency && (
                              <div className="text-xs text-stone-500 mt-0.5">
                                {URGENCY_LABEL[lead.urgency] || lead.urgency}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top">
                            <select
                              value={lead.status || "new"}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                              data-testid={`admin-status-select-${lead.id}`}
                              className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border-0 focus:outline-none focus:ring-2 focus:ring-stone-400 cursor-pointer ${STATUS_TINT[lead.status || "new"]}`}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                  {STATUS_LABEL[s]}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-5 py-4 align-top text-stone-600 max-w-[220px]">
                            {lead.property_address || <span className="text-stone-400">—</span>}
                            {lead.estimated_sqft != null && (
                              <div className="text-xs text-stone-500 mt-1">
                                ~ {Number(lead.estimated_sqft).toLocaleString()} sq ft
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top">
                            {lead.file_count > 0 ? (
                              <span className="inline-flex items-center gap-1.5 bg-teal-100 text-teal-800 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                                <FileText className="h-3 w-3" /> {lead.file_count}
                              </span>
                            ) : (
                              <span className="text-stone-400 text-xs">none</span>
                            )}
                          </td>
                          <td className="px-5 py-4 align-top text-right">
                            <button
                              type="button"
                              onClick={() => openLead(lead)}
                              data-testid={`admin-open-lead-${lead.id}`}
                              className="inline-flex items-center gap-1.5 bg-stone-900 text-white px-3.5 py-1.5 rounded-full text-xs font-semibold hover:bg-teal-700 transition-colors"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />

      {activeLead && (
        <LeadDetailDrawer
          lead={activeLead}
          loading={detailLoading}
          onClose={() => setActiveLead(null)}
        />
      )}
    </main>
  );
}

function StatCard({ label, value, tint = "stone", testId }) {
  const map = {
    stone: "bg-white border-stone-200 text-stone-900",
    teal: "bg-teal-50 border-teal-200 text-teal-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    rose: "bg-rose-50 border-rose-300 text-rose-900",
  };
  return (
    <div className={`border rounded-2xl p-5 ${map[tint]}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-wider opacity-70 font-semibold">
        {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function LeadDetailDrawer({ lead, loading, onClose }) {
  const tint = TINTS[lead.service_type] || TINTS.Other;
  const created = new Date(lead.created_at);
  const files = lead.existing_plan_files || [];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/50"
      onClick={onClose}
      data-testid="admin-lead-drawer"
    >
      <div
        className="w-full max-w-2xl bg-[#FAF7F2] h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#FAF7F2]/95 backdrop-blur-xl border-b border-stone-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-stone-500">Lead detail</div>
            <div className="font-display text-xl font-semibold">{lead.name}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-drawer-close"
            className="h-10 w-10 rounded-full border border-stone-300 bg-white flex items-center justify-center hover:bg-stone-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-block ${tint} px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider`}>
              {lead.service_type}
            </span>
            {lead.style && (
              <span className="bg-stone-100 text-stone-800 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                {lead.style}
              </span>
            )}
            {lead.urgency && (
              <span className="bg-rose-100 text-rose-800 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                {URGENCY_LABEL[lead.urgency] || lead.urgency}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-stone-500 ml-auto">
              <Calendar className="h-3.5 w-3.5" />
              {created.toLocaleString()}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow icon={<Mail className="h-4 w-4 text-teal-700" />} label="Email">
              <a
                href={`mailto:${lead.email}`}
                className="font-semibold text-stone-900 hover:text-teal-700 break-all"
                data-testid="admin-drawer-email"
              >
                {lead.email}
              </a>
            </InfoRow>
            <InfoRow icon={<Phone className="h-4 w-4 text-teal-700" />} label="Phone">
              {lead.phone ? (
                <a
                  href={`tel:${lead.phone}`}
                  className="font-semibold text-stone-900 hover:text-teal-700"
                  data-testid="admin-drawer-phone"
                >
                  {lead.phone}
                </a>
              ) : (
                <span className="text-stone-400">Not provided</span>
              )}
            </InfoRow>
            <InfoRow icon={<MapPin className="h-4 w-4 text-teal-700" />} label="Property address">
              {lead.property_address || <span className="text-stone-400">Not provided</span>}
            </InfoRow>
            <InfoRow icon={<FileText className="h-4 w-4 text-teal-700" />} label="Estimated sq ft">
              {lead.estimated_sqft != null ? (
                <span className="font-semibold">{Number(lead.estimated_sqft).toLocaleString()} sq ft</span>
              ) : (
                <span className="text-stone-400">Not provided</span>
              )}
            </InfoRow>
          </div>

          {lead.message && (
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">
                Message
              </div>
              <div
                className="bg-white border border-stone-200 rounded-2xl p-4 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap"
                data-testid="admin-drawer-message"
              >
                {lead.message}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">
              Uploaded files ({files.length})
            </div>
            {loading ? (
              <div className="text-stone-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Loading files…
              </div>
            ) : files.length === 0 ? (
              <div className="text-stone-400 text-sm">No files attached.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {files.map((f, i) => (
                  <FileTile key={`${f.filename || "file"}-${i}`} file={f} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5 flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-sm text-stone-700">{children}</div>
    </div>
  );
}

function FileTile({ file, index }) {
  const isImage = /^data:image\//i.test(file.data || "");
  const isPdf = /^data:application\/pdf/i.test(file.data || "") ||
    /\.pdf$/i.test(file.filename || "");

  return (
    <div
      className="bg-white border border-stone-200 rounded-2xl overflow-hidden"
      data-testid={`admin-drawer-file-${index}`}
    >
      <div className="aspect-[4/3] bg-stone-100 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={file.data}
            alt={file.filename}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="text-center text-stone-500 px-3">
            <FileText className="h-8 w-8 mx-auto mb-2" />
            <div className="text-xs font-semibold uppercase tracking-wider">
              {isPdf ? "PDF" : "File"}
            </div>
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <div className="text-xs text-stone-700 font-medium truncate" title={file.filename}>
          {file.filename}
        </div>
        <a
          href={file.data}
          download={file.filename}
          data-testid={`admin-drawer-file-download-${index}`}
          className="inline-flex items-center gap-1.5 bg-stone-900 text-white px-3 py-1.5 rounded-full text-[11px] font-semibold hover:bg-teal-700 transition-colors flex-shrink-0"
        >
          <Download className="h-3 w-3" /> Save
        </a>
      </div>
    </div>
  );
}
